import React, { FC, useEffect, useRef, useState } from 'react';
import { Dialog } from '@reach/dialog';
import * as Sentry from '@sentry/browser';
import '@reach/dialog/styles.css';
import { UseQueryResult, useMutation, useQuery, useQueryClient } from 'react-query';
import { css } from 'styled-components';
import { npBackendApi } from '../../api/npBackend';
import constants from '../../constants';
import { PaperClipIcon } from '../../svg/PaperClipIcon';
import { useLoginState } from '../auth/LoginContext';
import { LoginOrRegisterModal } from '../auth/LoginOrRegisterModal';
import {
    filledButton,
    buttonReset,
    fontSize,
    space,
    weight,
    secondaryButton,
    fontStyle,
    zIndex,
} from '../styles';
import { I18n, useDateFnsLocale, useI18n } from '../../i18n';
import { MoreRepliesIcon } from '../../svg/MoreRepliesIcon';
import { useDefaultProfilePicture } from '../../lib/useDefaultProfilePicture';
import { RemoveImageIcon } from '../../svg/RemoveImageIcon';
import { useLocation } from '@reach/router';
import RichTextSerializer from '../richTextSerializer';
import { format } from 'date-fns';
import { transparentize } from 'polished';
import { desktopMinWidthPx } from '../../templates/productPage/constants';

const POSTS_PER_PAGE = 10;

export const Discussion: FC<{ topic: string; introBlockText?: any; textareaLabel: string }> = ({
    topic,
    introBlockText,
    textareaLabel,
}) => {
    const isFirstLoad = useRef(true);
    const postsRef = useRef<HTMLDivElement>(null);
    const cache = useQueryClient();
    const [page, setPage] = useState(0);
    const getPostsResult = useQuery(
        ['discussion_posts', topic, page],
        () => getPosts(topic, page),
        {
            keepPreviousData: true,
            onError: error => {
                Sentry.captureException(error);
            },
        }
    );
    const pageCount = Math.ceil((getPostsResult.data?.nonPinnedCount || 1) / POSTS_PER_PAGE);
    let { t } = useI18n();
    let [showLoginModal, setShowLoginModal] = useState(false);
    const doShowLoginModal = () => {
        setShowLoginModal(true);
    };

    useEffect(() => {
        isFirstLoad.current = true;
    }, [topic]);

    useEffect(() => {
        if (postsRef.current && !getPostsResult.isFetching) {
            // Scroll to the top of the posts, but only when the user changes page, not on the first
            // load
            if (!isFirstLoad.current) {
                // https://stackoverflow.com/questions/49820013/javascript-scrollintoview-smooth-scroll-and-offset
                const bodyTop = document.body.getBoundingClientRect().top;
                let elementTop = postsRef.current.getBoundingClientRect().top;
                let scrollTo = elementTop - bodyTop - 150;
                // Only scroll upwards
                if (window.scrollY > scrollTo) {
                    window.scroll({
                        top: scrollTo,
                        left: 0,
                        behavior: 'smooth',
                    });
                }
            } else {
                isFirstLoad.current = false;
            }
        }
    }, [page, getPostsResult.isFetching, isFirstLoad]);

    return (
        <div
            css={`
                box-sizing: border-box;

                *,
                *:before,
                *:after {
                    box-sizing: inherit;
                }
                h2 {
                    ${fontSize.headinglarge};
                }
            `}
        >
            {showLoginModal && (
                <LoginOrRegisterModal
                    onSuccess={() => {
                        void cache.invalidateQueries('discussion_posts');
                    }}
                    doClose={() => {
                        setShowLoginModal(false);
                    }}
                />
            )}
            <h2>
                <I18n>discussion</I18n>
            </h2>
            {introBlockText && (
                <RichTextSerializer
                    sizes=""
                    displayOptions={{
                        // Does not matter, this does not support images
                        maxWidth: '100%',
                        maxWidthProductSelection: '100%',
                        textMaxWidth: '605px',
                    }}
                    content={introBlockText}
                    css={`
                        max-width: 605px;
                        margin-bottom: ${space(4)};
                        color: #737373;

                        a {
                            color: inherit;
                            ${weight.medium};
                        }
                    `}
                />
            )}
            <StarredPostPoll getPostsResult={getPostsResult} />
            <AddPostForm
                topic={topic}
                placeholder={t('discussionHelpUsImprove')}
                label={textareaLabel}
                showLoginModal={doShowLoginModal}
            />
            <div ref={postsRef}>
                <Posts
                    topic={topic}
                    getPostsResult={getPostsResult}
                    showLoginModal={doShowLoginModal}
                />
            </div>
            <Pagination
                currentPage={page}
                pageCount={pageCount}
                setPage={setPage}
                disabled={getPostsResult.isPreviousData}
            />
        </div>
    );
};

const Pagination: FC<{
    currentPage: number;
    pageCount: number;
    setPage: (newPage: number) => void;
    disabled: boolean;
}> = ({ currentPage, pageCount, setPage, disabled }) => {
    let pages = Array.from(Array(pageCount).keys());
    const [width, setWidth] = useState(500);
    const mobilePageBreak = 4;
    let mobileBreak = false;

    const handleWindowResize = () => {
        setWidth(window.innerWidth);
    };

    useEffect(() => {
        window.addEventListener('resize', handleWindowResize);
        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, []);

    if (pageCount === 1) {
        return null;
    }

    if (width < desktopMinWidthPx && pages.length + 1 > mobilePageBreak) {
        let p = pages.filter(element => element <= currentPage + 2 && element >= currentPage - 2);
        p.indexOf(0) === -1 ? p.unshift(0) : void 0;
        p.indexOf(pages.length - 1) === -1 ? p.push(pages.length - 1) : void 0;
        pages = p;
        mobileBreak = true;
    }

    return (
        <div
            css={`
                display: flex;
                margin-bottom: 100px;

                button {
                    ${buttonReset};
                    margin-right: 1em;
                    color: #737373;
                    font-weight: bold;
                }
            `}
        >
            {pages.map((page, i) => {
                if (currentPage == page) {
                    return (
                        <div
                            key={i}
                            css={`
                                box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.3);
                                padding: 4px 16px 5px;
                                font-weight: bold;
                                margin-right: 1em;
                                border-radius: 5px;
                                text-transform: uppercase;
                                display: flex;
                                align-items: center;
                                justify-content: center;

                                svg {
                                    margin-right: 5px;
                                }
                            `}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="9"
                                height="9"
                                fill="none"
                                viewBox="0 0 9 9"
                            >
                                <path
                                    fill="#000"
                                    d="M4.998 8.695l3.856-3.89L4.998.915 3.967 1.991l2.11 2.063H0v1.5h6.076l-2.11 2.062 1.032 1.078z"
                                ></path>
                            </svg>
                            <I18n>page</I18n> {page + 1}
                        </div>
                    );
                }

                return (
                    <React.Fragment key={i}>
                        {mobileBreak && currentPage > 2 && i == 1 && (
                            <div
                                css={`
                                    margin-right: 1em;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                `}
                            >
                                ...
                            </div>
                        )}
                        <button disabled={disabled} onClick={() => setPage(page)}>
                            {page + 1}
                        </button>
                        {mobileBreak &&
                            i == pages.length - 2 &&
                            page !== pages[pages.length - 1] - 1 && (
                                <div
                                    css={`
                                        margin-right: 1em;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                    `}
                                >
                                    ...
                                </div>
                            )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

async function addPostMutation({
    topic,
    content,
    parent,
    images,
}: {
    topic: string;
    content: string;
    parent?: number;
    images: string[];
}) {
    await npBackendApi.post(`/api/v1/community/discussion/${topic}/post`, {
        content,
        parent,
        images,
    });
}

const AddPostForm: FC<{
    topic: string;
    onSuccess?: () => void;
    parent?: number;
    label: string;
    placeholder: string;
    showLoginModal: () => void;
}> = ({ topic, label, placeholder, parent, onSuccess, showLoginModal }) => {
    const { t } = useI18n();
    const cache = useQueryClient();
    const fileUploadRef = useRef<HTMLInputElement>(null);
    const [images, setImages] = useState<string[]>([]);
    let [input, setInput] = useState('');

    let loginState = useLoginState();
    let { mutate, isLoading, error } = useMutation(addPostMutation, {
        onError: error => {
            Sentry.captureException(error);
        },
        onSuccess: () => {
            setInput('');
            setImages([]);
            void cache.invalidateQueries('discussion_posts');
            if (onSuccess) {
                onSuccess();
            }
        },
    });
    let postMessage = () => {
        void mutate({ topic, content: input, parent, images });
    };

    return (
        <>
            <input
                type="file"
                multiple
                ref={fileUploadRef}
                css={`
                    opacity: 0;
                    height: 0px;
                    width: 0px;
                    position: absolute;
                `}
                onChange={ev => {
                    Array.from(ev.target.files || []).forEach(file => {
                        const target = ev.target;
                        if (!file.type.startsWith('image/')) {
                            alert('We only support images');
                            return;
                        }
                        const reader = new FileReader();
                        reader.onload = e => {
                            if (e.target?.result) {
                                let result = e.target.result;
                                setImages(old => [...old, result as string]);
                                target.value = '';
                            }
                        };
                        reader.onerror = err => {
                            console.error(err);
                        };
                        reader.readAsDataURL(file);
                    });
                }}
            />
            {error && (
                <div
                    css={`
                        padding: ${space(1)} ${space(2)};
                        background: ${constants.colors.pallet.error};
                        color: white;
                        border-radius: 5px;
                        max-width: 605px;
                        margin: ${space(2)} 0;
                    `}
                >
                    {(error as Error).toString()}
                </div>
            )}
            <form
                onSubmit={ev => {
                    ev.preventDefault();
                    if (loginState.isLoggedIn) {
                        postMessage();
                    } else {
                        showLoginModal();
                    }
                }}
                css={`
                    width: 100%;
                    max-width: ${space(90)};
                    label {
                        display: block;
                    }
                `}
            >
                <label>
                    <div
                        css={`
                            margin-bottom: ${space(2)};
                            ${weight.medium};
                        `}
                    >
                        {label}
                    </div>
                    <div
                        css={`
                            display: flex;
                            flex-direction: column;

                            @media (min-width: 750px) {
                                align-items: center;
                                flex-direction: row;
                            }
                        `}
                    >
                        <div
                            css={`
                                flex-grow: 1;
                                margin-right: ${space(4)};
                                border-radius: 5px;
                                border: 2px solid rgba(0, 0, 0, 0.1);
                                max-width: 544px;
                            `}
                        >
                            <textarea
                                css={`
                                    display: block;
                                    height: ${space(16)};
                                    ${label && `margin-top: ${space(2)}`};
                                    padding: ${space(1)} ${space(2)};
                                    border: none;
                                    margin: 0;
                                    width: 100%;
                                    resize: none;
                                    margin: 0;
                                `}
                                placeholder={placeholder}
                                value={input}
                                onChange={ev => setInput(ev.target.value)}
                            />
                            <div
                                css={`
                                    display: flex;
                                    flex-wrap: wrap;
                                    align-items: flex-start;
                                `}
                            >
                                {images.map((src, idx) => (
                                    <div
                                        key={src}
                                        css={`
                                            position: relative;
                                        `}
                                    >
                                        <img
                                            src={src}
                                            css={`
                                                display: block;
                                                height: 64px;
                                                margin-left: ${space(2)};
                                                margin-bottom: ${space(2)};
                                                border-radius: 3px;
                                            `}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setImages(prevImages => {
                                                    let newImages = [...prevImages];
                                                    newImages.splice(idx, 1);
                                                    return newImages;
                                                });
                                            }}
                                            css={`
                                                ${buttonReset};
                                                position: absolute;
                                                right: -8px;
                                                top: -8px;
                                            `}
                                        >
                                            <RemoveImageIcon />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div
                            css={`
                                display: flex;
                                align-items: center;
                                margin-top: ${space(2)};
                                @media (min-width: 750px) {
                                    margin-top: 0;
                                }
                            `}
                        >
                            <button
                                css={`
                                    ${secondaryButton};
                                    margin-right: ${space(1)};
                                    height: 32px;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                `}
                                onClick={ev => {
                                    ev.preventDefault();
                                    if (fileUploadRef.current) {
                                        fileUploadRef.current.click();
                                    }
                                }}
                            >
                                <PaperClipIcon />
                            </button>
                            <button
                                css={`
                                    ${filledButton};
                                    height: 32px;
                                    padding-bottom: 5px;
                                    line-height: 25px;
                                `}
                                disabled={isLoading || input === ''}
                            >
                                {isLoading && loginState.isLoading ? t('loading') : t('publish')}
                            </button>
                        </div>
                    </div>
                </label>
            </form>
        </>
    );
};

type GetPosts = {
    starred: TPost[];
    pinned: TPost[];
    nonPinned: TPost[];
    nonPinnedCount: number;
};

type TPost = {
    id: number;
    topic: string;
    createdAt: Date;
    content: string;
    parent: number | null;
    images: string[];
    pinned: boolean;
    isCallout: boolean;
    calloutType?: string;
    title?: string;
    likes: {
        totalCount: number;
    };
    userLikes: {
        totalCount: number;
    };
    user?: {
        fullName?: string | null;
        admin: boolean;
        profilePicture?: string;
        id: number;
    };
    poll?: {
        id: number;
        votes: {
            totalCount: number;
        };
        userVotes: {
            nodes: Array<{
                alternativeId: number;
            }>;
        };
        pollAlternatives: {
            nodes: PollAlternative[];
        };
    };
    children?: {
        nodes: TPost[];
    };
};

type PollAlternative = {
    id: number;
    color: string;
    text: string;
    votes: {
        totalCount: number;
    };
};

// page is 0-indexed. 0 is the first page
async function getPosts(topic: string, page: number): Promise<GetPosts> {
    let res = await npBackendApi.get<GetPosts>(
        `/api/v1/community/discussion/${topic}/post?count=${POSTS_PER_PAGE}&offset=${
            POSTS_PER_PAGE * page
        }`
    );
    return res.data;
}

export const Posts: FC<{
    topic: string;
    getPostsResult: UseQueryResult<GetPosts>;
    showLoginModal: () => void;
}> = ({ topic, getPostsResult, showLoginModal }) => {
    const { data, error, isLoading } = getPostsResult;
    const location = useLocation();
    let makePostVisible: number | undefined = undefined;

    if (location.hash.startsWith('#post_')) {
        makePostVisible = parseInt(location.hash.substr(6));
    }

    useEffect(() => {
        if (location.hash.startsWith('#post_') && !isLoading) {
            // Scroll to post
            let element = document.querySelector(location.hash);
            const margin = 64 + 48;

            if (element) {
                // https://stackoverflow.com/questions/49820013/javascript-scrollintoview-smooth-scroll-and-offset
                const bodyTop = document.body.getBoundingClientRect().top;
                let elementTop = element.getBoundingClientRect().top;
                window.scroll({
                    top: elementTop - bodyTop - margin,
                    left: 0,
                });

                setTimeout(() => {
                    window.scroll({
                        top: elementTop - bodyTop - margin,
                        left: 0,
                    });
                }, 500);
            }
        }
    }, [location, isLoading]);

    if (error) {
        return <div>{(error as Error).toString()}</div>;
    }

    if (isLoading || !data) {
        return <div>Loading...</div>;
    }

    return (
        <div
            css={`
                margin-top: calc(${space(8)});
            `}
        >
            {data.pinned.length > 0 && (
                <div
                    css={`
                        /* Add 10 px here and remove them below to make the label start a bit
                           before the border */
                        margin-top: 10px;
                        border-left: 2px solid ${constants.colors.pallet.crowdsourcing};
                        padding-left: ${space(3)};
                    `}
                >
                    <div
                        css={`
                            ${fontStyle.label};
                            padding: ${space(0)} ${space(1)};
                            line-height: ${space(3)};
                            background: ${constants.colors.pallet.crowdsourcing};
                            color: white;
                            display: inline-block;
                            margin-left: calc(-${space(3)} - 2px);
                            border-radius: 3px;
                            position: relative;
                            /* Add 10 px above and remove them here to make this start a bit
                               before the border of the container */
                            top: -10px;
                            /* We move the box 10 px up, therefore we need to make the space 10px
                               smaller to make it work out */
                            margin-bottom: calc(${space(3)} - 10px);
                        `}
                    >
                        <I18n>pinned</I18n>
                    </div>
                    <PostGroup
                        topic={topic}
                        posts={data.pinned}
                        makePostVisible={makePostVisible}
                        showLoginModal={showLoginModal}
                    />
                </div>
            )}
            <PostGroup
                topic={topic}
                posts={data.nonPinned}
                makePostVisible={makePostVisible}
                showLoginModal={showLoginModal}
            />
        </div>
    );
};

export const PostGroup: FC<{
    topic: string;
    posts: TPost[];
    makePostVisible?: number;
    showLoginModal: () => void;
}> = ({ topic, posts, makePostVisible, showLoginModal }) => {
    return (
        <div>
            {posts.map(post => (
                <PostOuter
                    key={post.id}
                    post={post}
                    topic={topic}
                    makePostVisible={makePostVisible}
                    showLoginModal={showLoginModal}
                />
            ))}
        </div>
    );
};

export const PostOuter: FC<{
    post: TPost;
    topic: string;
    makePostVisible?: number;
    showLoginModal: () => void;
}> = ({ post, topic, makePostVisible, showLoginModal }) => {
    let [showAllChildren, setShowAllChildren] = useState(false);
    let children = post.children?.nodes || [];
    let visibleChildPosts = children.slice(showAllChildren ? 0 : children.length - 1);
    if (
        !showAllChildren &&
        makePostVisible &&
        !visibleChildPosts.some(p => p.id === makePostVisible)
    ) {
        visibleChildPosts = [
            ...children.filter(p => p.id === makePostVisible),
            ...visibleChildPosts,
        ];
    }

    if (post.isCallout) {
        return <Callout post={post} showLoginModal={showLoginModal} />;
    }

    return (
        <div
            css={`
                margin-bottom: ${space(6)};
            `}
        >
            {/* There are two ways of showing children. This one in PostOuter, with replies, and the simpler
                one inside <Post /> which does not allow to add more replies. We disable the later here
                because PostOuter shows the children differently */}
            <Post showChildren={false} post={post} showLoginModal={showLoginModal} />
            <div
                css={`
                    border-left: 2px solid rgba(0, 0, 0, 0.1);
                    padding: 0 ${space(3)};
                `}
            >
                {children.length > 1 && !showAllChildren && (
                    <button
                        onClick={() => setShowAllChildren(true)}
                        css={`
                            ${buttonReset};
                            ${fontStyle.label};
                            background: #fff;
                            box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.3);
                            padding: ${space(1)};
                            display: inline-block;
                            line-height: 1;
                            margin-bottom: ${space(5)};
                            border-radius: 5px;
                        `}
                    >
                        <MoreRepliesIcon /> {children.length - 1} <I18n>discussionMoreReplies</I18n>
                    </button>
                )}
                {visibleChildPosts.map(child => (
                    <Post
                        showChildren={true}
                        key={child.id}
                        post={child}
                        showLoginModal={showLoginModal}
                    />
                ))}
                <AddReply topic={topic} parent={post.id} showLoginModal={showLoginModal} />
            </div>
        </div>
    );
};

function calloutColor(type?: string): string {
    if (type === 'crowdfunding') {
        return constants.colors.pallet.crowdfunding;
    }
    if (type === 'crowdsourcing') {
        return constants.colors.pallet.crowdsourcing;
    }
    return '#2b2929';
}

const Callout: FC<{ post: TPost; showLoginModal: () => void }> = ({ post, showLoginModal }) => {
    const datefnsLocale = useDateFnsLocale();
    const color = calloutColor(post.calloutType);
    const [lightBox, setLightBox] = useState<string | undefined>(undefined);

    return (
        <div
            css={`
                background-color: ${transparentize(0.9, color)};
                color: ${color};
                border-left: 2px solid ${color};
                padding: ${space(2)} ${space(3)};
                margin-bottom: ${space(6)};
            `}
        >
            <LightBox src={lightBox} close={() => setLightBox(undefined)} />
            <div
                css={`
                    text-transform: uppercase;
                    ${fontStyle.label};
                `}
            >
                {format(new Date(post.createdAt), 'MMMM yyyy', {
                    locale: datefnsLocale,
                })}
            </div>
            <h3
                css={`
                    ${fontSize.large};
                    ${weight.regular};
                    margin: 0 0 ${space(1)};
                `}
            >
                {post.title}
            </h3>
            <div>
                <Content post={post} showLikeButton={false} showLoginModal={showLoginModal} />
            </div>
            <div
                css={`
                    display: flex;
                    flex-wrap: wrap;
                `}
            >
                {post.images.map(image => (
                    <img
                        loading="lazy"
                        css={`
                            border-radius: 5px;
                            margin-bottom: ${space(3)};
                            margin-right: ${space(1)};
                            cursor: pointer;
                        `}
                        key={image}
                        src={`${process.env.GATSBY_IMAGE_HOST}${image}?height=200`}
                        onClick={() => {
                            setLightBox(`${process.env.GATSBY_IMAGE_HOST}${image}?height=1000`);
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

async function voteOnAlternativeMutation({ alternativeId }: { alternativeId: number }) {
    try {
        await npBackendApi.post(`/api/v1/community/polls/alternative/${alternativeId}/vote`, {
            alternativeId,
        });
    } catch (err: any) {
        let maybeMessage = err.response?.data?.message;
        if (maybeMessage) {
            throw new Error(maybeMessage);
        }
        throw err;
    }
}

export const Post: FC<{ post: TPost; showChildren: boolean; showLoginModal: () => void }> = ({
    post,
    showChildren,
    showLoginModal,
}) => {
    let [lightBox, setLightBox] = useState<string | undefined>(undefined);

    let tag = post.user?.admin ? 'team' : null;
    let defaultProfilePicture = useDefaultProfilePicture(post.user?.id || 0);

    let profilePicture: string | null = post.user?.profilePicture
        ? `${process.env.GATSBY_IMAGE_HOST}${post.user?.profilePicture}?w=50&h=50&fit=crop`
        : defaultProfilePicture;

    if (post.user?.fullName === 'Slettet konto') {
        profilePicture = null;
    }

    return (
        <>
            <LightBox src={lightBox} close={() => setLightBox(undefined)} />
            <div
                id={`post_${post.id}`}
                css={`
                    ${weight.medium};
                    margin-bottom: ${space(2)};
                    display: flex;
                    align-items: center;
                `}
            >
                {profilePicture && (
                    <img
                        loading="lazy"
                        css={`
                            display: block;
                            width: 25px;
                            height: 25px;
                            border-radius: 5px;
                            margin-right: ${space(2)};
                        `}
                        alt=""
                        src={profilePicture}
                    />
                )}
                {post.user?.fullName || 'Anonymous'}
                {tag && (
                    <div
                        css={`
                            ${fontStyle.label};
                            background: #cee9f2;
                            padding: 0 ${space(1)};
                            border-radius: 5px;
                            margin-left: ${space(2)};
                        `}
                    >
                        {tag}
                    </div>
                )}
            </div>
            <div
                css={`
                    margin-bottom: ${space(3)};
                    max-width: 544px;
                    overflow-wrap: break-word;
                    a {
                        color: inherit;
                        ${weight.medium};
                    }
                `}
            >
                <Content showLoginModal={showLoginModal} post={post} />
            </div>
            <div
                css={`
                    display: flex;
                    flex-wrap: wrap;
                `}
            >
                {post.images.map(image => (
                    <img
                        loading="lazy"
                        css={`
                            border-radius: 5px;
                            margin-bottom: ${space(3)};
                            margin-right: ${space(1)};
                            cursor: pointer;
                        `}
                        key={image}
                        src={`${process.env.GATSBY_IMAGE_HOST}${image}?height=200`}
                        onClick={() => {
                            setLightBox(`${process.env.GATSBY_IMAGE_HOST}${image}?height=1000`);
                        }}
                    />
                ))}
            </div>
            <PostPoll {...{ post }} />
            {post.children && post.children.nodes.length > 0 && showChildren && (
                <div
                    css={`
                        border-left: 2px solid rgba(0, 0, 0, 0.1);
                        padding: 0 ${space(3)};
                        margin-bottom: ${space(2)};
                    `}
                >
                    {post.children?.nodes.map(child => (
                        <Post
                            showChildren={true}
                            key={child.id}
                            post={child}
                            showLoginModal={showLoginModal}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

const URL_REGEX = /(\b(?:https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[A-Z0-9+&@#/%=~_|])/gim;

const Content: FC<{ post: TPost; showLoginModal: () => void; showLikeButton?: boolean }> = ({
    post,
    showLoginModal,
    showLikeButton: showLike = true,
}) => {
    const content = post.content;

    function parseParagraph(paragraph: string) {
        return paragraph.split('\n').map((line, idx, all) => {
            let parts: (React.ReactElement | string)[] = line.split(URL_REGEX);

            for (let i = 1; i < parts.length; i += 2) {
                parts[i] = (
                    <a
                        target="_blank"
                        // https://support.google.com/webmasters/answer/96569?hl=en
                        rel="noreferrer noopener nofollow ugc"
                        key={`link ${i}`}
                        href={parts[i] as string}
                    >
                        {parts[i]}
                    </a>
                );
            }

            return (
                <React.Fragment key={idx}>
                    {parts}
                    {idx + 1 === all.length ? null : <br />}
                </React.Fragment>
            );
        });
    }

    return (
        <>
            {content.split('\n\n').map((paragraph, idx, contents) => (
                <p
                    css={`
                        margin: ${space(2)} 0;
                    `}
                    key={idx}
                >
                    {/* We want spacing before the like-button, but we cannot use margin-left
                        because that would mean we would have spacing also when the like button
                        is on line 2. Instead add a &nbsp; */}
                    {parseParagraph(paragraph)}&nbsp;{' '}
                    {showLike && idx === contents.length - 1 && (
                        <LikeButton showLoginModal={showLoginModal} post={post} />
                    )}
                </p>
            ))}
        </>
    );
};

async function likeMutation({ postId, like }: { postId: number; like: boolean }) {
    try {
        if (like) {
            await npBackendApi.post(`/api/v1/community/discussion/posts/${postId}/like`);
        } else {
            await npBackendApi.delete(`/api/v1/community/discussion/posts/${postId}/like`);
        }
    } catch (err: any) {
        let maybeMessage = err.response?.data?.message;
        if (maybeMessage) {
            throw new Error(maybeMessage);
        }
        throw err;
    }
}

const LikeButton: FC<{ post: TPost; showLoginModal: () => void }> = ({ post, showLoginModal }) => {
    const { t } = useI18n();
    const cache = useQueryClient();
    let loginState = useLoginState();

    let {
        mutate: setLike,
        isLoading,
        error,
    } = useMutation(likeMutation, {
        onError: error => {
            Sentry.captureException(error);
        },
        onSuccess: () => {
            void cache.invalidateQueries('discussion_posts');
        },
    });

    if (isLoading) {
        return (
            <div
                css={`
                    display: inline-block;
                    border-bottom: 1px solid #d6d6d6;
                `}
            >
                <LikedIcon /> ...
            </div>
        );
    }

    if (error) {
        return (
            <div
                css={`
                    display: inline-block;
                    color: ${constants.colors.pallet.error};
                `}
            >
                {(error as Error).toString()}
            </div>
        );
    }

    if (post.userLikes.totalCount > 0) {
        return (
            <button
                onClick={() => setLike({ postId: post.id, like: false })}
                css={`
                    ${buttonReset};
                    color: #2b2929;
                    display: inline-block;
                    border-bottom: 1px solid transparent;
                    &:hover {
                        border-bottom: 1px solid #737373;
                    }
                `}
            >
                <LikedIcon /> {post.likes.totalCount}
            </button>
        );
    }

    return (
        <button
            onClick={() => {
                if (loginState.isLoggedIn) {
                    void setLike({ postId: post.id, like: true });
                } else {
                    showLoginModal();
                }
            }}
            css={`
                ${buttonReset};
                color: #737373;
                border-bottom: 1px solid #d6d6d6;
                &:hover {
                    --like-stroke: #2b2929;
                    color: #2b2929;
                }
            `}
            title={t('like this')}
        >
            <LikeIcon /> {post.likes.totalCount === 0 ? <I18n>Like</I18n> : post.likes.totalCount}
        </button>
    );
};

const LightBox: FC<{ src?: string; close: () => void }> = ({ src, close }) => {
    return (
        <Dialog
            css={`
                background: none;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 80vw;
                height: 80vh;

                @media (max-width: 800px) {
                    position: fixed;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: 0;
                    z-index: ${zIndex.modal};
                    width: 100vw;
                    height: 100vh;
                    margin: 0;
                    padding: 0;
                }

                box-sizing: border-box;
                *,
                *:before,
                *:after {
                    box-sizing: inherit;
                }
            `}
            isOpen={src !== undefined}
            onDismiss={close}
            onClick={event => {
                if (event.target === event.currentTarget) {
                    close();
                }
            }}
        >
            <div
                css={`
                    position: relative;
                    padding: ${space(1)};
                    background: #fff;
                    border-radius: 3px;
                    min-width: 200px;
                    min-height: 200px;
                    max-height: 80vh;
                    max-width: 80vw;

                    @media (max-width: 800px) {
                        max-width: 100vw;
                        max-height: 100vh;
                    }
                `}
            >
                <button
                    className="close-button"
                    onClick={close}
                    css={`
                        ${buttonReset};
                        font-size: 55px;
                        position: absolute;
                        bottom: -27px;
                        right: 0px;
                        color: #f9f9f9;
                    `}
                >
                    ×
                </button>
                <img
                    css={`
                        display: block;
                        border-radius: 3px;
                        width: 100%;
                        height: 100%;
                        max-width: 100%;
                        max-height: calc(80vh - ${space(2)});
                        object-fit: cover;

                        @media (max-width: 800px) {
                            max-height: 100%;
                            object-fit: contain;
                        }
                    `}
                    src={src!}
                />
            </div>
        </Dialog>
    );
};

const StarredPostPoll: FC<{ getPostsResult: UseQueryResult<GetPosts> }> = ({ getPostsResult }) => {
    const { data } = getPostsResult;
    if (!data) {
        // Will show "loading" in the post list;
        return <div />;
    }

    let starred = data.starred[0];

    if (!starred) {
        return <div />;
    }

    return (
        <div>
            <div
                css={`
                    margin-bottom: ${space(1)};
                    ${weight.medium};
                `}
            >
                {starred.content}
            </div>
            <PostPoll
                post={starred}
                css={`
                    && {
                        @media (min-width: 800px) {
                            flex-direction: row;
                            fieldset {
                                flex-direction: row;
                            }
                            button,
                            div {
                                margin-right: ${space(2)};
                            }
                        }
                    }
                `}
            />
        </div>
    );
};

const PostPoll: FC<{ post: TPost; className?: string }> = ({ post, className }) => {
    const cache = useQueryClient();
    let [showLoginModal, setShowLoginModal] = useState(false);
    let votedBeforeModal = useRef<null | number>(null);
    let loginState = useLoginState();
    let {
        mutate: vote,
        isLoading,
        error,
    } = useMutation<
        void,
        Error,
        {
            alternativeId: number;
        }
    >(voteOnAlternativeMutation, {
        onError: error => {
            Sentry.captureException(error);
        },
        onSuccess: () => {
            void cache.invalidateQueries('discussion_posts');
        },
    });

    if (!post.poll) {
        return null;
    }

    let userVotedAlternative: number | undefined = post.poll.userVotes.nodes.map(
        vote => vote.alternativeId
    )[0];

    return (
        <>
            {showLoginModal && (
                <LoginOrRegisterModal
                    onSuccess={() => {
                        void cache.invalidateQueries('discussion_posts');
                        if (votedBeforeModal.current) {
                            void vote({ alternativeId: votedBeforeModal.current });
                        }
                    }}
                    doClose={() => {
                        setShowLoginModal(false);
                    }}
                />
            )}
            {error && (
                <div
                    role="alert"
                    css={`
                        padding: ${space(1)} ${space(2)};
                        background: ${constants.colors.pallet.error};
                        color: white;
                        border-radius: 5px;
                        max-width: 605px;
                        margin: ${space(2)} 0;
                    `}
                >
                    {error.message || error.toString()}
                </div>
            )}
            <div
                className={className}
                css={`
                    margin-bottom: ${space(2)};
                    display: flex;
                    align-items: flex-start;
                    flex-direction: column;

                    fieldset {
                        display: flex;
                        align-items: flex-start;
                        flex-direction: column;

                        button {
                            display: flex;
                            align-items: center;
                        }
                    }
                `}
            >
                <PostPollInner
                    userVotedAlternative={userVotedAlternative}
                    poll={post.poll}
                    isLoading={isLoading}
                    onClickVote={alternative => {
                        if (loginState.isLoggedIn) {
                            void vote({ alternativeId: alternative.id });
                        } else {
                            votedBeforeModal.current = alternative.id;
                            setShowLoginModal(true);
                        }
                    }}
                />
            </div>
        </>
    );
};

const PostPollInner: FC<{
    userVotedAlternative: number;
    poll: NonNullable<TPost['poll']>;
    isLoading: boolean;
    onClickVote: (alternative: PollAlternative) => void;
}> = ({ userVotedAlternative, poll, isLoading, onClickVote }) => {
    if (userVotedAlternative) {
        let totalVotes = poll.votes.totalCount;
        return (
            <>
                {poll.pollAlternatives.nodes.map((alternative, idx) => (
                    <div
                        css={`
                            ${secondaryButton};
                            cursor: default;
                            margin-bottom: ${space(2)};
                            width: auto;
                            ${userVotedAlternative === alternative.id &&
                            css`
                                background: #2b2929;
                                color: #fff;
                            `}
                        `}
                        key={alternative.id}
                    >
                        <span
                            css={`
                                ${fontStyle.label};
                                display: inline-block;
                                width: ${space(2)};
                                height: ${space(2)};
                                line-height: ${space(2)};
                                background: #d6d6d6;
                                border-radius: 3px;
                                margin-right: ${space(2)};
                                text-align: center;
                                color: #2b2929;
                            `}
                        >
                            {ALPHABET[idx] || ''}
                        </span>
                        {alternative.text}{' '}
                        <span
                            css={`
                                color: #737373;
                            `}
                        >
                            ({Math.round((alternative.votes.totalCount / totalVotes) * 100)} %)
                        </span>
                    </div>
                ))}
            </>
        );
    }

    return (
        <>
            <fieldset disabled={isLoading}>
                {poll.pollAlternatives.nodes.map((alternative, idx) => (
                    <button
                        css={`
                            ${secondaryButton};
                            display: block;
                            margin-bottom: ${space(2)};
                        `}
                        key={alternative.id}
                        onClick={() => onClickVote(alternative)}
                    >
                        <span
                            css={`
                                ${fontStyle.label};
                                display: inline-block;
                                width: ${space(2)};
                                height: ${space(2)};
                                line-height: ${space(2)};
                                background: #d6d6d6;
                                border-radius: 3px;
                                margin-right: ${space(2)};
                            `}
                        >
                            {ALPHABET[idx] || ''}
                        </span>
                        {alternative.text}
                    </button>
                ))}
            </fieldset>
        </>
    );
};

export const AddReply: FC<{ topic: string; parent: number; showLoginModal: () => void }> = ({
    topic,
    parent,
    showLoginModal,
}) => {
    const [showForm, setShowForm] = useState(false);
    const { t } = useI18n();

    if (showForm) {
        return (
            <AddPostForm
                onSuccess={() => setShowForm(false)}
                topic={topic}
                parent={parent}
                placeholder={t('Write a reply...')}
                label=""
                showLoginModal={showLoginModal}
            />
        );
    }

    return (
        <button
            css={`
                ${buttonReset};
                color: #c0c0c0;
                display: flex;
                align-items: center;
                svg {
                    transform: translateY(2px);
                    margin-right: 8px;
                }
            `}
            onClick={() => {
                setShowForm(true);
            }}
        >
            <ReplyIcon />
            <I18n>Write a reply...</I18n>
        </button>
    );
};

const ALPHABET = 'abcdefghijklmnopqrstuvwxyzæøå';

const ReplyIcon: FC = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="17"
            height="17"
            fill="none"
            viewBox="0 0 17 17"
        >
            <path
                fill="#737373"
                d="M5 12.47h.75a.75.75 0 00-.75-.75v.75zM5 16h-.75a.75.75 0 001.246.562L5 16zm4-3.53v-.75a.75.75 0 00-.496.188l.496.563zM1.75 2A.25.25 0 012 1.75V.25A1.75 1.75 0 00.25 2h1.5zm0 9.47V2H.25v9.47h1.5zm.25.25a.25.25 0 01-.25-.25H.25c0 .967.784 1.75 1.75 1.75v-1.5zm3 0H2v1.5h3v-1.5zM5.75 16v-3.53h-1.5V16h1.5zm2.754-4.092l-4 3.53.992 1.124 4-3.529-.992-1.125zM15 11.721H9v1.5h6v-1.5zm.25-.25a.25.25 0 01-.25.25v1.5a1.75 1.75 0 001.75-1.75h-1.5zm0-9.471v9.47h1.5V2h-1.5zM15 1.75a.25.25 0 01.25.25h1.5A1.75 1.75 0 0015 .25v1.5zm-13 0h13V.25H2v1.5z"
            ></path>
        </svg>
    );
};

const LikedIcon: FC = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="16"
            fill="none"
            viewBox="0 0 15 16"
        >
            <path
                fill="#D6D6D6"
                stroke="#2B2929"
                strokeWidth="1.5"
                d="M1 8a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1H2a1 1 0 01-1-1V8z"
            ></path>
            <path
                fill="#fff"
                fillRule="evenodd"
                d="M9.472 1.78a1.843 1.843 0 00-2.48.803l-1.78 3.482a1.834 1.834 0 00-.2.928.006.006 0 01-.006.007.006.006 0 00-.006.006V14a1 1 0 001 1h2.146a5 5 0 004.864-3.842l.697-2.926A1 1 0 0012.734 7h-2.226a1 1 0 01-.89-1.455l.656-1.285a1.843 1.843 0 00-.802-2.48z"
                clipRule="evenodd"
            ></path>
            <path
                fill="#2B2929"
                d="M6.992 2.583l.668.341-.668-.341zm2.48-.803l.34-.667-.34.667zm-4.26 4.285l-.667-.341.668.34zm7.798 5.093l-.73-.174.73.174zM10.274 4.26l.668.341-.668-.341zM5.013 6.993l-.75.038.75-.038zM7.66 2.924c.275-.537.933-.75 1.47-.476l.683-1.335A2.593 2.593 0 006.324 2.24l1.336.683zM5.88 6.406l1.78-3.482-1.336-.683-1.78 3.483 1.337.682zm-.118.55c-.01-.184.028-.372.119-.55l-1.336-.682a2.584 2.584 0 00-.281 1.307l1.498-.075zM5.75 14V7.006h-1.5V14h1.5zm2.396.25H6v1.5h2.146v-1.5zm4.134-3.266a4.25 4.25 0 01-4.134 3.266v1.5a5.75 5.75 0 005.594-4.418l-1.46-.348zm.697-2.926l-.697 2.926 1.46.348.696-2.927-1.459-.347zm-2.47-.308h2.227v-1.5h-2.226v1.5zm-.9-3.831L8.95 5.204l1.335.682.657-1.285-1.336-.682zm-.477-1.47c.538.274.75.932.476 1.47l1.336.682a2.593 2.593 0 00-1.13-3.488L9.13 2.448zM4.25 14c0 .966.784 1.75 1.75 1.75v-1.5a.25.25 0 01-.25-.25h-1.5zm6.258-7.75a.25.25 0 01-.223-.364L8.95 5.204a1.75 1.75 0 001.558 2.546v-1.5zm-5.502 0a.756.756 0 00-.756.756h1.5c0 .411-.333.744-.744.744v-1.5zm9.43 2.155a1.75 1.75 0 00-1.702-2.155v1.5c.161 0 .28.15.243.308l1.46.347zM4.264 7.031a.744.744 0 01.742-.781v1.5a.756.756 0 00.756-.794l-1.498.075z"
            ></path>
        </svg>
    );
};

const LikeIcon: FC = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="16"
            fill="none"
            viewBox="0 0 15 16"
        >
            <path
                fill="#D6D6D6"
                // stroke="#737373"
                strokeWidth="1.5"
                css={`
                    stroke: var(--like-stroke, #737373);
                `}
                d="M1 7.422a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1H2a1 1 0 01-1-1v-6z"
            ></path>
            <path
                // fill="#737373"
                css={`
                    fill: var(--like-stroke, #737373);
                `}
                d="M6.992 2.005l.668.34-.668-.34zm2.48-.803l.34-.668-.34.668zm-4.26 4.285l-.667-.341.668.34zm7.798 5.093l-.73-.174.73.174zm-2.736-6.898l.668.341-.668-.341zM5.013 6.415l-.75.037.75-.037zm2.647-4.07c.275-.537.933-.75 1.47-.475L9.813.534a2.593 2.593 0 00-3.489 1.13l1.336.682zM5.88 5.829l1.78-3.482-1.336-.683-1.78 3.483 1.337.682zm-.118.55c-.01-.184.028-.372.119-.55l-1.336-.682a2.584 2.584 0 00-.281 1.306l1.498-.074zm-.012 7.044V6.428h-1.5v6.994h1.5zm2.396.25H6v1.5h2.146v-1.5zm4.134-3.266a4.25 4.25 0 01-4.134 3.266v1.5a5.75 5.75 0 005.594-4.418l-1.46-.348zm.697-2.926l-.697 2.926 1.46.348.696-2.927-1.459-.347zm-2.47-.308h2.227v-1.5h-2.226v1.5zm-.9-3.832L8.95 4.626l1.335.682.657-1.285-1.336-.683zM9.13 1.87c.538.275.75.933.476 1.47l1.336.683A2.593 2.593 0 009.812.534L9.13 1.87zM4.25 13.422c0 .966.784 1.75 1.75 1.75v-1.5a.25.25 0 01-.25-.25h-1.5zm6.258-7.75a.25.25 0 01-.223-.364L8.95 4.626a1.75 1.75 0 001.558 2.546v-1.5zm-5.502 0a.756.756 0 00-.756.756h1.5c0 .41-.333.744-.744.744v-1.5zm9.43 2.155a1.75 1.75 0 00-1.702-2.155v1.5c.161 0 .28.15.243.308l1.46.347zM4.264 6.452a.744.744 0 01.742-.78v1.5a.756.756 0 00.756-.794l-1.498.074z"
            ></path>
        </svg>
    );
};
